/**
 * Display Container
 *
 * Creates the host DOM element for a form based on its displayType.
 * The returned `host` element is passed as the Shadow DOM root to renderForm().
 *
 * Supported display types: Popup, Inline.
 */

// ============================================================================
// Types
// ============================================================================

export interface DisplayContainer {
  /** Element passed as `host` to renderForm() */
  host: HTMLElement;
  /** Remove all created DOM elements and event listeners */
  cleanup: () => void;
}

export interface DisplayContainerOptions {
  displayType: "Popup" | "Inline";
  /** Show the close (X) button. Defaults to true. Only applies to Popup. */
  showCloseButton?: boolean;
  /** Target element to render into. Required for Inline display type. */
  targetElement?: HTMLElement;
}

// ============================================================================
// Shared: Close Button
// ============================================================================

function createCloseButton(): HTMLButtonElement {
  const closeBtn = document.createElement("button");
  closeBtn.setAttribute("aria-label", "Close");
  Object.assign(closeBtn.style, {
    position: "absolute",
    top: "8px",
    right: "8px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "20px",
    lineHeight: "1",
    zIndex: "1",
    color: "#666",
    padding: "4px 8px",
  });
  closeBtn.textContent = "\u2715"; // ✕
  return closeBtn;
}

// ============================================================================
// Factory
// ============================================================================

export function createDisplayContainer(
  options: DisplayContainerOptions,
): DisplayContainer {
  if (options.displayType === "Inline" && options.targetElement) {
    return createInlineContainer(options.targetElement);
  }
  const showClose = options.showCloseButton ?? true;
  return createPopupContainer(showClose);
}

// ============================================================================
// Inline
// ============================================================================

function createInlineContainer(target: HTMLElement): DisplayContainer {
  // The shadow host is the target element itself — renderForm() will
  // call attachShadow() on it, isolating form styles from the page.
  return {
    host: target,
    cleanup: () => {
      // Clear shadow DOM content
      if (target.shadowRoot) {
        while (target.shadowRoot.firstChild) {
          target.shadowRoot.removeChild(target.shadowRoot.firstChild);
        }
      }
    },
  };
}

// ============================================================================
// Popup
// ============================================================================

function createPopupContainer(showCloseButton: boolean): DisplayContainer {
  // Fullscreen overlay
  const overlay = document.createElement("div");
  overlay.setAttribute("data-sf-overlay", "popup");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: "999999",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });

  // Popup box — outer wrapper, holds close button + shadow host as siblings
  const box = document.createElement("div");
  box.setAttribute("data-sf-form", "popup");
  Object.assign(box.style, {
    position: "relative",
    maxWidth: "480px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "8px",
  });

  const cleanup = () => {
    overlay.remove();
    document.removeEventListener("keydown", keydownHandler);
  };

  // Close button (X) — light DOM child of box, rendered as a sibling of the
  // shadow host so it remains visible after attachShadow() is called
  if (showCloseButton) {
    const closeBtn = createCloseButton();
    box.appendChild(closeBtn);
    closeBtn.addEventListener("click", cleanup);
  }

  // Inner element that receives attachShadow() — sibling of the close button
  const shadowHost = document.createElement("div");
  box.appendChild(shadowHost);

  overlay.appendChild(box);
  document.body.appendChild(overlay);

  // Close on overlay background click
  overlay.addEventListener("click", (e: MouseEvent) => {
    if (e.target === overlay) cleanup();
  });

  // Close on Escape key
  const keydownHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") cleanup();
  };
  document.addEventListener("keydown", keydownHandler);

  return { host: shadowHost, cleanup };
}
